import { cast, flow, types } from "mobx-state-tree";

import { FeatureFlagResponse } from "../../api-models/response/FeatureFlagResponse";
import { endpoints } from "../api/ApiEndpoints";
import { api } from "../api/ApiService";
import { StorageModel } from "../storage/StorageModel";
import { FeatureFlag } from "./FeatureFlag";
import { Flags } from "../../generated/FeatureFlags";

const FeatureFlagsModel = types.model({
  featureFlags: types.optional(types.array(types.frozen<FeatureFlag>()), []),
  flagsWereChanged: false,
});

const FeatureFlagsStorageModel = types
  .compose(FeatureFlagsModel, StorageModel)
  .named("FeatureFlagsStorageModel")
  .actions((self) => {
    return {
      mapResponse(featureFlags: FeatureFlagResponse[]) {
        self.featureFlags = cast(featureFlags.map((x) => new FeatureFlag(x)));
      },
      loadFeatureFlagsOnDemand: flow(function* () {
        if (!self.featureFlags.length) {
          self.flagsWereChanged = false;
          yield self.load();
        }
      }),
      isFeatureEnabled(featureFlag: Flags) {
        if (!self.featureFlags.length) return false;
        return self.featureFlags.find((x) => x.name === featureFlag)?.isEnabled ?? false;
      },
      resetFeatureFlags() {
        self.flagsWereChanged = true;
        self.featureFlags = cast([]);
      },
      disabledApps() {
        var disabledApps = 0;
        if (!this.isFeatureEnabled(Flags.Enable_Ocr)) disabledApps += 1;
        return disabledApps;
      }
    };
  })
  .views((self) => ({
    get endpoint() {
      const endpoint = endpoints.get("feature_flags");
      return endpoint;
    },
    get isLoading() {
      // See StorageModel.loadAttempted: an empty flag list after a completed load is a real
      // answer, not a pending one.
      if (self.error || self.loadAttempted) return false;
      return !self.featureFlags.length && !self.flagsWereChanged;
    },
  }));

// Feature flags are global (per app-alias, not per org) and the backend endpoint is
// [AllowAnonymous], so autoload for everyone — including guests — instead of waiting on
// an organization subscription key that a signed-out user never gets.
export const featureFlagsStorage = FeatureFlagsStorageModel.create({}, { api });
